import React from "react";

import { CANVAS_HEIGHT, CANVAS_WIDTH } from "../constants";
import { renderStroke } from "../strokeRender";
import { deserializeStrokes } from "../strokeSerialize";
import useReferenceJson from "./useReferenceJson";

// Shared submission-render plumbing: fetches the per-marc reference,
// pulls strokes either from `item.strokesBlob` (Mine, IndexedDB) or
// `item.strokesUrl` (gallery / admin), rasterizes them once to a PNG
// data URL, and merges the blob's optional `overrides` bag (creative
// mode) over the canonical reference values. Callers wire the returned
// values into their own JSX.
async function rasterizeStrokes(strokes, palette) {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext("2d");
  for (const s of strokes) renderStroke(ctx, s, palette);
  return canvas.toDataURL("image/png");
}

export default function useSubmissionRender(item) {
  const { reference } = useReferenceJson(item.marcId, false);
  const [drawingSrc, setDrawingSrc] = React.useState(null);
  const [overrides, setOverrides] = React.useState(null);
  // Deserialized strokes + palette stick around so the composite-action
  // menu can re-render through the shared composite renderer instead of
  // re-deserializing on download.
  const [strokes, setStrokes] = React.useState(null);
  const [palette, setPalette] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const handle = async (blob) => {
      if (cancelled || !blob) return;
      const deserialized = await deserializeStrokes(blob.strokes || []);
      if (cancelled) return;
      setOverrides(blob.overrides ?? null);
      setStrokes(deserialized);
      setPalette(blob.palette || []);
      const src = await rasterizeStrokes(deserialized, blob.palette || []);
      if (!cancelled) setDrawingSrc(src);
    };
    if (item.strokesBlob) {
      handle(item.strokesBlob);
    } else if (item.strokesUrl) {
      fetch(item.strokesUrl)
        .then((r) => {
          if (!r.ok) throw new Error(`strokes ${r.status}`);
          return r.json();
        })
        .then(handle)
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [item.strokesBlob, item.strokesUrl]);

  const eggTransform = overrides?.eggTransform ?? reference?.egg?.transform;
  const lights = overrides?.eggLights ?? reference?.egg?.lights ?? [];
  const warmth = overrides?.warmth ?? reference?.egg?.warmth ?? 30;
  const redshift = overrides?.redshift ?? reference?.egg?.redshift ?? 30;
  const marcTransform = overrides?.marcTransform ?? reference?.marc?.transform;

  return {
    drawingSrc,
    eggTransform,
    lights,
    warmth,
    redshift,
    marcTransform,
    strokes,
    palette,
  };
}
